import axios, { AxiosRequestConfig, AxiosResponse, AxiosError, Method, ResponseType } from "axios";
import { APICallResult } from "./types";
import { Headers } from "./types";

declare global {
  interface Window {
    numberOfAjaxCallPending: number;
    numberOfAjaxCallErrors: number;
    numberOfAjaxCallsResponseInterceptorID: number | null;
    numberOfAjaxCallsRequestInterceptorID: number | null;
  }
}
window.numberOfAjaxCallPending = window.numberOfAjaxCallPending | 0;
window.numberOfAjaxCallErrors = window.numberOfAjaxCallErrors | 0;
window.numberOfAjaxCallsResponseInterceptorID = null;
window.numberOfAjaxCallsRequestInterceptorID = null;

export class ApiControllerSettings {
  /** The base url of the API */
  public static iProvaApiUrl: string;
  /** Whether logging should be enabled */
  public static enableLogging: boolean = false;
  /** The cross site request forgery cookie name */
  public static xsrfCookieName: string = "__AntiCSRFToken";
  /** The cross site request forgery header name */
  public static xsrfHeaderName: string = "RequestVerificationToken";
}

export type RequestCollector = {
  requests: { method: string; path: string; response: AxiosResponse<any>; start: number; end: number }[];
  Stop: () => void;
};

export default class ApiController {
  private _withCredentials: boolean = true;
  private static _requestCollectors: RequestCollector[] = [];

  //constructor
  public constructor() {
    if (window.numberOfAjaxCallsRequestInterceptorID === null) {
      window.numberOfAjaxCallsRequestInterceptorID = axios.interceptors.request.use(
        (requestConfig: any) => {
          window.numberOfAjaxCallPending++;
          return requestConfig;
        },
        (error: any) => {
          window.numberOfAjaxCallPending--;
          window.numberOfAjaxCallErrors++;
          return Promise.reject(error);
        }
      );
    }
    if (window.numberOfAjaxCallsResponseInterceptorID === null) {
      window.numberOfAjaxCallsResponseInterceptorID = axios.interceptors.response.use(
        (response: any) => {
          window.numberOfAjaxCallPending--;
          return response;
        },
        (error: any) => {
          window.numberOfAjaxCallPending--;
          window.numberOfAjaxCallErrors++;
          return Promise.reject(error);
        }
      );
    }
  }

  /**
   * Execute a GET request
   * @param path request path
   * @param suppressErrors whether to supress the throwing of exceptions on error
   * @param headers headers to send with the request
   */
  protected async $get<ReturnType>(path: string, suppressErrors?: boolean, headers?: Headers): Promise<APICallResult<ReturnType>> {
    return this.executeRequest<void, ReturnType>("GET", path, undefined, suppressErrors, headers);
  }

  /**
   * Execute a PATCH request
   * @param path request path
   * @param data request body data
   * @param suppressErrors whether to supress the throwing of exceptions on error
   * @param headers headers to send with the request
   */
  protected async $patch<Input, ReturnType>(
    path: string,
    data: Input,
    suppressErrors?: boolean,
    headers?: Headers
  ): Promise<APICallResult<ReturnType>> {
    return this.executeRequest<Input, ReturnType>("PATCH", path, data, suppressErrors, headers);
  }

  /**
   * Execute a POST request
   * @param path request path
   * @param data request body data
   * @param suppressErrors whether to supress the throwing of exceptions on error
   * @param headers headers to send with the request
   */
  protected async $post<Input, ReturnType>(
    path: string,
    data: Input,
    suppressErrors?: boolean,
    headers?: Headers
  ): Promise<APICallResult<ReturnType>> {
    return this.executeRequest<Input, ReturnType>("POST", path, data, suppressErrors, headers);
  }

  /**
   * Execute a DELETE request
   * @param path request path
   * @param suppressErrors whether to supress the throwing of exceptions on error
   * @param headers headers to send with the request
   */
  protected async $delete<InputType, ReturnType>(
    path: string,
    data?: InputType,
    suppressErrors?: boolean,
    headers?: Headers
  ): Promise<APICallResult<ReturnType>> {
    return this.executeRequest<InputType | undefined, ReturnType>("DELETE", path, data, suppressErrors, headers);
  }

  /**
   * Execute a PUT request
   * @param path request path
   * @param data request body data
   * @param suppressErrors whether to supress the throwing of exceptions on error
   * @param headers headers to send with the request
   */
  protected async $put<Input, ReturnType>(
    path: string,
    data: Input,
    suppressErrors?: boolean,
    headers?: Headers
  ): Promise<APICallResult<ReturnType>> {
    return this.executeRequest<Input, ReturnType>("PUT", path, data, suppressErrors, headers);
  }

  /**
   * Execute a reaquest
   * @param method HTTP method to use
   * @param path request path
   * @param data request body data
   * @param headers headers to send with the request
   */
  private async executeRequest<Input, ReturnType>(
    method: Method,
    path: string,
    data: Input,
    suppressErrors?: boolean,
    headers?: Headers
  ): Promise<APICallResult<ReturnType>> {
    // ensure suppress errors is true as default
    if (suppressErrors === undefined) suppressErrors = true;

    //wrap axios request promise in a promise to only return the response data
    return new Promise<APICallResult<ReturnType>>((resolveFunction, rejectFunction) => {
      if (ApiControllerSettings.enableLogging) this.logRequest(method, path);
      const requestConfig: AxiosRequestConfig = {
        data: this.getData(data, headers),
        baseURL: ApiControllerSettings.iProvaApiUrl,
        url: path,
        method: method,
        withCredentials: this._withCredentials,
        xsrfCookieName: ApiControllerSettings.xsrfCookieName,
        xsrfHeaderName: ApiControllerSettings.xsrfHeaderName,
        headers: headers || {},
        responseType: this.getResponseType(headers)
      };

      const start = performance.now();
      const requestCollectors = [...ApiController._requestCollectors];

      axios
        .request(requestConfig)
        .then((response: AxiosResponse<any>) => {
          if (ApiControllerSettings.enableLogging) this.logResponse(method, path, response);

          const result = <APICallResult<ReturnType>>{
            success: true,
            headers: response.headers,
            reasonPhrase: response.statusText,
            statusCode: response.status,
            result: response.data,
            response: response
          };

          requestCollectors.forEach(collector => collector.requests.push({ method, path, response, start, end: performance.now() }));

          resolveFunction(result);
        })
        .catch((error: AxiosError) => {
          //check if it's API error
          if (error.response) {
            if (error.response.status == 401) {
              //authentication failure
              const authErrorEvent = new CustomEvent("autherror", {
                detail: error
              });
              window.dispatchEvent(authErrorEvent);
            }

            if (suppressErrors) {
              const result = <APICallResult<ReturnType>>{
                success: false,
                headers: error.response.headers,
                reasonPhrase: error.response.statusText,
                statusCode: error.response.status,
                result: error.response.data,
                response: error.response
              };

              //call resolve
              resolveFunction(result);
            } else {
              rejectFunction(error);
            }
          } else {
            if (suppressErrors) {
              const afterApiError = new CustomEvent("afterapierror", {
                detail: error
              });

              window.dispatchEvent(afterApiError);
            } else {
              rejectFunction(error);
            }
          }
        });
    });
  }

  public static RegisterRequestCollector(): RequestCollector {
    const collector = {
      requests: [],
      Stop: () => {
        const index = this._requestCollectors.indexOf(collector);
        if (index !== -1) {
          this._requestCollectors.splice(index, 1);
        }
      }
    } as RequestCollector;

    // Add the listner
    this._requestCollectors.push(collector);

    return collector;
  }

  /**
   * Write request info to log
   * @param method request's HTTP method
   * @param path request path
   */
  private logRequest(method: string, path: string) {
    if (ApiControllerSettings.enableLogging) console.log(`ApiController: ${method} ${path}`);
  }

  /**
   * Write response to log
   * @param method request's HTTP method
   * @param path request path
   * @param response response object
   */
  private logResponse(method: string, path: string, response: AxiosResponse<any>) {
    if (ApiControllerSettings.enableLogging) console.log(`ApiController: ${method} ${path} \n- Response status: ${response.status}`);
  }

  /**
   *Returns processed data based on content type
   * @param data request body data
   * @param headers headers that are send to request
   */
  private getData(data: any, headers?: Headers): any {
    if (headers && headers["Content-Type"] === "application/x-www-form-urlencoded") {
      return Object.keys(data)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
        .join("&");
    }
    return data;
  }

  /**
   * Returns expected response type
   * @param data request body data
   * @param headers headers that are send to request
   */
  private getResponseType(headers?: Headers): ResponseType {
    if (headers && headers["Accept"] === "text/plain") {
      return "text";
    }
    return "json";
  }
}
